import { useCallback, useContext, useEffect, useRef } from "react";
import { UseCases, Boundaries } from "@planzone/core";
import { CoreContext } from "@planzone/view-models";
import {
  AllocationWarning,
  CheckAllocationWarningsRequest,
} from "@planzone/dto";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { useModal } from "@root/client/modal/utils";
import {
  useRequestInterceptor,
  intercepteables,
  InterceptorSignal,
} from "./requestInterceptor";

function buildCheckRequest(
  useCase: UseCases,
  request: any,
): { checkRequest: CheckAllocationWarningsRequest; date: string } | null {
  switch (useCase) {
    case UseCases.allocateResources: {
      const { task, allocations } =
        request as Boundaries["allocateResources"]["request"];
      if (allocations.length === 0) return null;
      return {
        checkRequest: {
          taskUuid: task.uuid,
          allocations: allocations.map((a) => ({
            resourceUuid: a.resource.uuid,
          })),
        },
        date: task.startDate,
      };
    }
    case UseCases.createTask: {
      const { task } = request as Boundaries["createTask"]["request"];
      if (!task.allocations || task.allocations.length === 0) return null;
      return {
        checkRequest: {
          taskDraft: {
            startDate: task.startDate,
            endDate: task.endDate,
            allocations: task.allocations.map((a) => ({
              resourceUuid: a.resource.uuid,
            })),
          },
        },
        date: task.startDate,
      };
    }
    case UseCases.editAllocation: {
      const { allocation, nextAllocation } =
        request as Boundaries["editAllocation"]["request"];
      const taskUuid = allocation.task?.uuid;
      if (!taskUuid) return null;
      return {
        checkRequest: {
          taskUuid,
          allocations: [{ resourceUuid: nextAllocation.resource.uuid }],
        },
        date: allocation.task?.startDate ?? "",
      };
    }
    default:
      return null;
  }
}

function AllocationWarningsGuard() {
  const { core } = useContext(CoreContext);
  const { pushView } = useModal();
  const { registerInterceptor } = useRequestInterceptor();

  const coreRef = useRef(core);
  coreRef.current = core;

  const pushViewRef = useRef(pushView);
  pushViewRef.current = pushView;

  const pendingResolveRef = useRef<
    ((warnings: AllocationWarning[]) => void) | null
  >(null);

  const handleEvent = useCallback(
    ({ type, data }: { type: string; data: any }) => {
      if (!pendingResolveRef.current) return;

      if (type === events.checkAllocationWarningsSucceeded) {
        pendingResolveRef.current(data.warnings);
        pendingResolveRef.current = null;
      } else if (type === events.checkAllocationWarningsFailed) {
        pendingResolveRef.current([]);
        pendingResolveRef.current = null;
      }
    },
    [],
  );

  useMessage(handleEvent);

  const interceptor = useCallback(
    (useCase: UseCases, request: any): Promise<InterceptorSignal> => {
      if (!intercepteables.includes(useCase)) {
        return Promise.resolve("proceed");
      }

      const checkData = buildCheckRequest(useCase, request);
      if (!checkData) return Promise.resolve("proceed");

      const { checkRequest, date } = checkData;

      return new Promise<InterceptorSignal>((resolve) => {
        pendingResolveRef.current = (warnings: AllocationWarning[]) => {
          if (warnings.length === 0) {
            resolve("proceed");
            return;
          }

          pushViewRef.current({
            key: UseCases.checkAllocationWarnings,
            data: {
              warnings,
              date,
              onConfirm: () => resolve("proceed"),
              onClose: () => resolve("abort"),
            },
          });
        };

        coreRef.current.checkAllocationWarnings(checkRequest);
      });
    },
    [],
  );

  useEffect(() => {
    return registerInterceptor(interceptor);
  }, [interceptor, registerInterceptor]);

  return null;
}

export { AllocationWarningsGuard };
